import { ClearOutlined } from "@ant-design/icons";
import { Button } from "antd";
import Title from "antd/es/typography/Title";
import { useFormikContext } from "formik";
import { ExecuteReportValues } from "shared/types";
import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import { ReportItem } from "shared/api";
import * as Yup from "yup";
import { ExecuteReports, ReportsData } from "./ExecuteReportForm";

interface ExportReportHeaderProps {
  pdfUrl: string | null;
  selectedReport?: ReportItem;
  executeReports: ExecuteReports;
  isValid: boolean;
  validationSchema?: Yup.AnyObjectSchema;
  getReportData: ReportsData;
  clearPdfUrl: () => void;
}

const ExportReportHeader: FC<ExportReportHeaderProps> = ({
  pdfUrl,
  selectedReport,
  executeReports,
  isValid,
  validationSchema,
  getReportData,
  clearPdfUrl,
}) => {
  const { t } = useTranslation("", { keyPrefix: "executeReports" });

  const { values, resetForm } = useFormikContext<ExecuteReportValues>();

  const clearFormAndResetPdfFile = () => {
    resetForm();
    clearPdfUrl();
  };

  // The preview blob is always generated in the format selected on the page
  // (see useExecuteReportsRequests), so the same blob can be downloaded
  // directly without re-asking the format or re-fetching from the server.
  const handleDownload = () => {
    if (pdfUrl && selectedReport) {
      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = `${selectedReport.name}.${values.report_format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const isExcelReport =
    values.report_format === "xlsx" || values.report_format === "xls";

  return (
    <div className="w-full flex items-center gap-x-4">
      <Title level={3}>
        {t("selectedReport", { name: selectedReport?.name })}
      </Title>

      {values?.id && (
        <ClearOutlined
          onClick={clearFormAndResetPdfFile}
          className="mb-4 self-end !text-red-100 scale-150"
          type="primary"
        />
      )}

      <Button
        onClick={executeReports.handleExecuteReport}
        className="btn-primary--dark min-w-32 !m-0"
        type="primary"
        loading={executeReports.isLoadingReportExport}
        disabled={!isValid || !validationSchema}
      >
        {t("exportReport")}
      </Button>

      <Button
        onClick={getReportData.handleGetReportData}
        className="bg-green-300 w-32"
        type="primary"
        loading={getReportData.isLoadingReportData}
        disabled={!isValid || !validationSchema}
      >
        {t("getData")}
      </Button>

      {pdfUrl && (
        <>
          <Button
            className="bg-green-100"
            type="primary"
            onClick={handleDownload}
          >
            {t("downloadFormat", {
              format: values.report_format.toUpperCase(),
            })}
          </Button>

          <Button type="primary" danger onClick={clearFormAndResetPdfFile}>
            {isExcelReport
              ? t("clearFormat", {
                  format: values.report_format.toUpperCase(),
                })
              : t("clearPdf")}
          </Button>
        </>
      )}
    </div>
  );
};

export { ExportReportHeader };
